import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { iconChevronDown } from '@sorare/core/src/atoms/icons/system/iconChevronDown.ts';
import { iconChevronUp } from '@sorare/core/src/atoms/icons/system/iconChevronUp.ts';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock.ts';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';

import type { LeagueTrckGroupSelect_so5LeagueTrackGroup } from './__generated__/index.graphql.ts';

const StyledButton = styled(Button)`
  overflow: hidden;
`;
const StyledLabelL = styled(LabelL)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const StyledScarcityIcon = styled(ScarcityIcon)`
  flex-shrink: 0;
`;
const Option = styled(SBHorizontal)`
  cursor: pointer;
  gap: var(--unit);
  min-width: 320px;
  padding: var(--intermediate-unit) var(--double-unit);
  transition: 0.2s ease-in-out;
  .composable &:hover {
    background-color: var(--c-nd-50);
  }
`;
const OptionContainer = styled.div`
  position: relative;
  &:not(.composable) {
    opacity: 0.5;
    &:hover {
      opacity: 0.65;
    }
  }
`;

type Props = {
  selectedSo5LeagueTrckGroupSlug: string;
  so5LeagueTrckGroups: LeagueTrckGroupSelect_so5LeagueTrackGroup[];
  onClick: (args: { trckGrp: string; trck: string }) => void;
  allowNavigatingWhenLocked?: boolean;
};

export const LeagueTrckGroupSelect = ({
  selectedSo5LeagueTrckGroupSlug,
  so5LeagueTrckGroups,
  onClick,
  allowNavigatingWhenLocked = false,
}: Props) => {
  const selectedSo5LeagueTrckGroup =
    so5LeagueTrckGroups.find(
      ({ slug }) => selectedSo5LeagueTrckGroupSlug === slug
    ) || so5LeagueTrckGroups[0];

  return (
    <Dropdown
      gap={4}
      label={({ open, ...btnProps }) => (
        <StyledButton
          disableDebounce
          color="tertiary"
          size="small"
          startIcon={
            <StyledScarcityIcon
              rarity={selectedSo5LeagueTrckGroup?.mainRarityType || 'mix'}
            />
          }
          endIcon={
            <FontAwesomeIcon icon={open ? iconChevronUp : iconChevronDown} />
          }
          className={classNames({ active: open })}
          {...btnProps}
        >
          <StyledLabelL>
            {selectedSo5LeagueTrckGroup?.displayName || '-'}
          </StyledLabelL>
        </StyledButton>
      )}
    >
      {({ closeDropdown }) => (
        <>
          {so5LeagueTrckGroups.map(so5LeagueTrckGroup => {
            const composableTrcks = so5LeagueTrckGroup.so5LeagueTracks.filter(
              ({ canCompose }) => canCompose.value || allowNavigatingWhenLocked
            );
            const composableTrck =
              composableTrcks.find(
                ({ seasonality }) =>
                  seasonality === So5LeaderboardSeasonality.IN_SEASON
              ) || composableTrcks[0];

            const onClickCb = () => {
              onClick({
                trckGrp: so5LeagueTrckGroup.slug,
                trck: composableTrck?.slug || '',
              });
              closeDropdown();
            };

            const isClickable = composableTrck || allowNavigatingWhenLocked;

            const contenders = so5LeagueTrckGroup.so5LeagueTracks.flatMap(
              ({ mySo5LeaderboardContenders }) => mySo5LeaderboardContenders
            );
            const contendersWithALineup = contenders.filter(
              ({ so5Lineup }) => so5Lineup?.confirmable && !so5Lineup.draft
            );
            const showChip = contenders.length > 0;

            return (
              <OptionContainer
                key={so5LeagueTrckGroup.slug}
                className={classNames({
                  composable: !!isClickable,
                })}
              >
                <Option
                  {...(isClickable
                    ? {
                        as: ButtonBase,
                        replace: true,
                        onClick: onClickCb,
                      }
                    : {})}
                >
                  <Horizontal>
                    <ScarcityIcon
                      rarity={so5LeagueTrckGroup.mainRarityType || 'mix'}
                    />
                    <LabelL
                      color={
                        selectedSo5LeagueTrckGroupSlug ===
                        so5LeagueTrckGroup.slug
                          ? 'var(--c-white)'
                          : 'var(--c-nd-600)'
                      }
                    >
                      {so5LeagueTrckGroup?.displayName}
                    </LabelL>
                  </Horizontal>
                  {isClickable ? (
                    <>
                      {!!showChip && (
                        <Chip color="green">
                          {contendersWithALineup.length}
                        </Chip>
                      )}
                    </>
                  ) : (
                    <FontAwesomeIcon icon={iconLock} />
                  )}
                </Option>
              </OptionContainer>
            );
          })}
        </>
      )}
    </Dropdown>
  );
};

LeagueTrckGroupSelect.fragments = {
  so5LeagueTrackGroup: gql`
    fragment LeagueTrckGroupSelect_so5LeagueTrackGroup on So5LeagueTrackGroup {
      slug
      mainRarityType
      displayName
      so5LeagueTracks {
        slug
        seasonality
        canCompose {
          value
        }
        mySo5LeaderboardContenders {
          slug
          so5Lineup {
            id
            draft
            confirmable
          }
        }
      }
    }
  ` as TypedDocumentNode<LeagueTrckGroupSelect_so5LeagueTrackGroup>,
};
